import { ContentType, Placement } from '@ori/analytics-data';
import type {
  CopyLinkPanelProps,
  SharedContent,
  SharingPanelProps,
  VendorType,
} from '@ori/sharing-panel';
import { share, SharingCapability } from '@ori/sharing-panel';
import { Dialog } from '@ori-ui/components';
import { styled } from '@ori-ui/mui';
import { lazy, Suspense, useCallback, useState } from 'react';
import type { FC } from 'react';

import { Loader } from './Loader';
import type { ShareButtonProps } from './ShareButton';
import { ShareButton } from './ShareButton';
import { useTranslations } from '~/hooks/useTranslations';
import { translationKeys as translations } from '~/models';
import {
  getSharedContent,
  logDebug,
  showErrorNotification,
  translationKeys,
  triggerShareItemClickEvent,
} from '~/utils';

export interface SharingWrapperProps {
  isSharingButtonActive: boolean;
}

const SharingPopup = styled(Dialog)<{
  ownerState: { isCopyLinkOpen: boolean };
}>(({ ownerState: { isCopyLinkOpen }, theme }) => ({
  ...(isCopyLinkOpen && {
    '&.MuiOriDialog-header': {
      paddingBottom: theme.spacing(6),
    },
    '&.MuiOriDialog-content': {
      maxWidth: 540,
    },
  }),
}));

const CopyLinkPanel = lazy(async () => import('./CopyLinkPanel'));
const SharingPanel = lazy(async () => import('./SharingPanel'));

export const SharingWrapper: FC<SharingWrapperProps> = ({ isSharingButtonActive }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isCopyLinkOpen, setIsCopyLinkOpen] = useState(false);
  const [sharedContent, setSharedContent] = useState<Partial<SharedContent>>({});
  const [link, setLink] = useState('');
  const isPopupOpen = isPanelOpen || isCopyLinkOpen;

  const { translate } = useTranslations();

  const onShareButtonClick = useCallback(async () => {
    logDebug('onShareButtonClick()');
    try {
      const data = await getSharedContent();
      setSharedContent(data);
      setIsPanelOpen(true);
    } catch (error: unknown) {
      showErrorNotification(translationKeys.genericError);
    }
  }, []);

  const onClose = useCallback(() => {
    logDebug('Closing sharing wrapper ...');
    setIsPanelOpen(false);
    setIsCopyLinkOpen(false);
  }, []);

  const onError = useCallback(() => {
    onClose();
    showErrorNotification(translationKeys.genericError);
  }, [onClose]);

  const onCopyLink = useCallback((vendor: VendorType, finalSharedLink: string) => {
    logDebug('onCopyLink()', { finalSharedLink, vendor });
    setLink(finalSharedLink);
    setIsCopyLinkOpen(true);
  }, []);

  const onShareIconClick = useCallback(
    async (vendor: VendorType) => {
      logDebug('onShareIconClick handler called.');
      // close sharing popup
      onClose();
      // always monitor user intentions even when the checks later will fail
      triggerShareItemClickEvent({
        contentType: ContentType.ProductList,
        method: vendor,
        placement: Placement.Minibag,
      });
      // start the sharing process
      void share({ onCopyLink, onError, sharedContent, vendor });
    },
    [onClose, onCopyLink, onError, sharedContent],
  );

  const shareButtonProps: ShareButtonProps = {
    isSharingButtonActive,
    onClick: onShareButtonClick,
  };

  const { ShareUrlAndText } = SharingCapability;
  const panelProps: SharingPanelProps = {
    // sharing capabilities filter so far hard-coded here
    capabilities: [ShareUrlAndText],
    captionText: translate(translations.shareWithBrandPartner),
    isCopyLinkIconDisplayed: true,
    onError,
    onShareIconClick,
  };

  const copyLinkPopupProps = { header: translate(translations.copyLink) } as const;
  const sharePopupProps = { size: 'small' } as const;
  const popupProps = {
    disableEnforceFocus: true,
    disableTransition: true,
    onClose,
    open: isPopupOpen,
    ownerState: { isCopyLinkOpen },
    ...(isCopyLinkOpen ? copyLinkPopupProps : sharePopupProps),
  } as const;

  const copyLinkProps: CopyLinkPanelProps = {
    buttonText: translate(translations.copy),
    copiedText: translate(translations.linkCopied),
    link,
  };

  logDebug('Rendering SharingWrapper.tsx, popupProps:', popupProps);

  return (
    <>
      <ShareButton {...shareButtonProps} />
      {isPopupOpen ? (
        <SharingPopup {...popupProps}>
          {isPanelOpen ? (
            <Suspense fallback={<Loader />}>
              <SharingPanel {...panelProps} />
            </Suspense>
          ) : null}
          {isCopyLinkOpen ? (
            <Suspense fallback={<Loader />}>
              <CopyLinkPanel {...copyLinkProps} />
            </Suspense>
          ) : null}
        </SharingPopup>
      ) : null}
    </>
  );
};

export default SharingWrapper;
